import React, { useState, useEffect, useRef } from 'react';
import { Typography, Table, Button, Input, InputNumber, Switch, message, Modal, Form, Tag } from 'antd';
import { PlusOutlined, EditOutlined } from '@ant-design/icons';
import api from '../api';
import LinksFormList, { cleanLinks } from '../components/LinksFormList';

// Admin > Recap Templates: the text for month-end coaching recaps, with
// {{variables}} filled in per agent on Generate Coaching Recap.

export default function RecapTemplatesPage() {
  const [templates, setTemplates] = useState([]);
  const [variables, setVariables] = useState([]);
  const [loading,   setLoading]   = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editTpl,   setEditTpl]   = useState(null);
  const [saving,    setSaving]    = useState(false);
  const [form] = Form.useForm();

  // The text field a variable button inserts into, and where the cursor was.
  const focusRef = useRef({ field: 'notes_template', start: null, end: null });
  const trackFocus = (field) => (e) => {
    focusRef.current = { field, start: e.target.selectionStart, end: e.target.selectionEnd };
  };

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/recap-templates');
      setTemplates(res.data);
    } catch {
      message.error('Failed to load recap templates.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    api.get('/api/recap-templates/variables').then((r) => setVariables(r.data)).catch(() => {});
  }, []);

  const openCreate = () => {
    setEditTpl(null);
    form.resetFields();
    form.setFieldsValue({ is_active: true, error_section_threshold: 0 });
    focusRef.current = { field: 'notes_template', start: null, end: null };
    setModalOpen(true);
  };

  const openEdit = (t) => {
    setEditTpl(t);
    form.setFieldsValue({
      name:                   t.Name,
      title_template:         t.TitleTemplate,
      notes_template:         t.NotesTemplate || '',
      comment_template:       t.CommentTemplate || '',
      ai_examples:            t.AIExamples || '',
      bottom_score_threshold: t.BottomScoreThreshold,
      top_score_threshold:    t.TopScoreThreshold,
      error_section_threshold: t.ErrorSectionThreshold ?? 0,
      default_links:          t.DefaultLinks || [],
      is_active:              !!t.IsActive,
    });
    focusRef.current = { field: 'notes_template', start: null, end: null };
    setModalOpen(true);
  };

  const insertVariable = (key) => {
    const { field, start, end } = focusRef.current;
    const token = `{{${key}}}`;
    const value = form.getFieldValue(field) || '';
    const s = start ?? value.length;
    const e = end ?? value.length;
    form.setFieldValue(field, value.slice(0, s) + token + value.slice(e));
    focusRef.current = { field, start: s + token.length, end: s + token.length };
  };

  const handleSave = async () => {
    let values;
    try { values = await form.validateFields(); } catch { return; }
    setSaving(true);
    try {
      const payload = {
        name:                   values.name,
        title_template:         values.title_template,
        notes_template:         values.notes_template || null,
        comment_template:       values.comment_template || null,
        ai_examples:            values.ai_examples || null,
        bottom_score_threshold: values.bottom_score_threshold ?? null,
        top_score_threshold:    values.top_score_threshold ?? null,
        error_section_threshold: values.error_section_threshold ?? 0,
        default_links:          cleanLinks(values.default_links),
        is_active:              values.is_active,
      };
      if (editTpl) {
        await api.put(`/api/recap-templates/${editTpl.ID}`, payload);
        message.success('Template updated.');
      } else {
        await api.post('/api/recap-templates', payload);
        message.success('Template created.');
      }
      setModalOpen(false);
      load();
    } catch (err) {
      message.error(err.response?.data?.error || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (t) => {
    try {
      await api.put(`/api/recap-templates/${t.ID}`, { is_active: !t.IsActive });
      setTemplates((prev) => prev.map((x) => x.ID === t.ID ? { ...x, IsActive: t.IsActive ? 0 : 1 } : x));
    } catch {
      message.error('Failed to update.');
    }
  };

  const columns = [
    { title: 'Name', dataIndex: 'Name', key: 'Name', width: 240, sorter: (a, b) => a.Name.localeCompare(b.Name) },
    { title: 'Coaching Title', dataIndex: 'TitleTemplate', key: 'TitleTemplate', ellipsis: true },
    { title: 'Highest Section must be at least', dataIndex: 'TopScoreThreshold', key: 'TopScoreThreshold', width: 220,
      render: (v) => (v == null ? '—' : `${Number(v)}%`) },
    { title: 'Lowest Section must be below', dataIndex: 'BottomScoreThreshold', key: 'BottomScoreThreshold', width: 200,
      render: (v) => (v == null ? '—' : `${Number(v)}%`) },
    { title: 'Active', dataIndex: 'IsActive', key: 'IsActive', width: 90,
      render: (v, r) => <Switch checked={!!v} size="small" onChange={() => toggleActive(r)} /> },
    { title: '', key: 'actions', width: 70, align: 'right',
      render: (_, r) => <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)}>Edit</Button> },
  ];

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <Typography.Title level={4} style={{ margin: 0 }}>Recap Templates</Typography.Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>Add Template</Button>
      </div>

      <Table
        rowKey="ID"
        dataSource={templates}
        columns={columns}
        loading={loading}
        size="small"
        pagination={false}
        locale={{ emptyText: 'No recap templates yet.' }}
      />

      <Modal maskClosable={false}
        title={editTpl ? 'Edit Recap Template' : 'New Recap Template'}
        open={modalOpen}
        onOk={handleSave}
        onCancel={() => setModalOpen(false)}
        confirmLoading={saving}
        okText="Save"
        width={860}
        destroyOnClose
      >
        <div style={{ marginBottom: 12 }}>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>Variables</Typography.Text>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 6 }}>
            {variables.map((v) => (
              <Tag key={v.key} style={{ cursor: 'pointer', marginInlineEnd: 0 }} onMouseDown={(e) => e.preventDefault()}
                   onClick={() => insertVariable(v.key)}>
                {`{{${v.key}}}`}
              </Tag>
            ))}
          </div>
        </div>

        <Form form={form} layout="vertical">
          <Form.Item label="Name" name="name" rules={[{ required: true, message: 'Required' }]}>
            <Input maxLength={100} />
          </Form.Item>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            <Form.Item label="Highest Section must be at least" name="top_score_threshold">
              <InputNumber min={0} max={100} step={0.5} addonAfter="%" style={{ width: 160 }} />
            </Form.Item>
            <Form.Item label="Lowest Section must be below" name="bottom_score_threshold">
              <InputNumber min={0} max={100} step={0.5} addonAfter="%" style={{ width: 160 }} />
            </Form.Item>
            <Form.Item label="Minimum Section Errors" name="error_section_threshold">
              <InputNumber min={0} precision={0} style={{ width: 120 }} />
            </Form.Item>
            <Form.Item label="Active" name="is_active" valuePropName="checked">
              <Switch />
            </Form.Item>
          </div>
          <Form.Item label="Coaching Title" name="title_template" rules={[{ required: true, message: 'Required' }]}>
            <Input maxLength={300} onSelect={trackFocus('title_template')} onFocus={trackFocus('title_template')} />
          </Form.Item>
          <Form.Item label="Notes" name="notes_template">
            <Input.TextArea rows={8} onSelect={trackFocus('notes_template')} onFocus={trackFocus('notes_template')} />
          </Form.Item>
          <Form.Item label="Comments" name="comment_template">
            <Input.TextArea rows={5} onSelect={trackFocus('comment_template')} onFocus={trackFocus('comment_template')} />
          </Form.Item>
          {/* Finished recaps for Draft with AI to copy the style of. */}
          <Form.Item label="AI Examples" name="ai_examples">
            <Input.TextArea rows={8} />
          </Form.Item>
          <Form.Item label="Default Links">
            <LinksFormList name="default_links" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}

